/**
 * Sources Spotlight Tooltip (Evidence onboarding step 3/3).
 * Anchors to the source control button in the chat input.
 * Opens when the user clicks "Next" on the Session Chat Sidebar spotlight.
 * Structure and style match SessionAskHeidiSpotlight and SessionChatSidebarSpotlight; uses source-control-spotlight-visual.svg.
 * On "Try now": closes the spotlight and opens the source control popover.
 * @see https://www.figma.com/design/PiRSH2ORtexkp82fSPpi9Z/Evidence-led-sign-up---onboarding?node-id=6664-25315
 */

import sourceControlSpotlightVisual from '@/assets/image/evidence/source-control-spotlight-visual.svg';
import { openSourceControlPopoverAtom } from '@/atoms/sourceControlAtoms';;
import { sourcesSpotlightOpenAtom } from '@/atoms/evidenceOnboardingAtoms';
import {
  IS_EVIDENCE_LAUNCH_TRIAL_AND_UPGRADE_FLOWS_ENABLED,
} from '@/features/flags/legacy-flags';
import { Button } from '@heidi/design-system/button';
import {
  Spotlight,
  SpotlightAnchor,
  SpotlightArrow,
  SpotlightClose,
  SpotlightContent,
  SpotlightDescription,
  SpotlightFooter,
  SpotlightHeader,
  SpotlightImage,
  SpotlightProgress,
  SpotlightTitle,
} from '@heidi/design-system/spotlight';
import { useQueryUser } from '@heidi/workspace/data';
import { TrackingEventName } from '@/models/analytics/eventTypes';
import { ScreenName } from '@heidi/runtime/analytics-vocabulary';
import { SignupSource } from '@heidi/schema/ml-scribe';
import { useTranslations } from '@heidi/runtime/i18n';
import { useAtom, useSetAtom } from 'jotai';
import Image from 'next/image';
import { useCallback, useEffect, useMemo, type ReactNode } from 'react';
import { track } from '@/lib/analytics/track';

const SPOTLIGHT_SIDE_OFFSET = 12;

interface SourcesSpotlightTooltipProps {
  /** Children to wrap with SpotlightAnchor (the source control button) */
  children: ReactNode;
}

export const SourcesSpotlightTooltip = ({ children }: SourcesSpotlightTooltipProps) => {
  const { t } = useTranslations();
  const [isOpen, setIsOpen] = useAtom(sourcesSpotlightOpenAtom);
  const setOpenSourceControlPopover = useSetAtom(openSourceControlPopoverAtom);
  const { data: signupSource } = useQueryUser((data) => data?.signup_source);

  const isFlowEnabled = IS_EVIDENCE_LAUNCH_TRIAL_AND_UPGRADE_FLOWS_ENABLED();
  const shouldShowSpotlight = useMemo(
    () => isFlowEnabled && signupSource === SignupSource.scribe,
    [isFlowEnabled, signupSource]
  );

  useEffect(() => {
    if (isOpen) {
      track({ options: { amplitude: false },
        event_name: TrackingEventName.ModalViewed,
        props: { screen_name: ScreenName.SourcesSpotlightTooltip },
      });
    }
  }, [isOpen]);

  const handleOpenChange = useCallback(
    (open: boolean) => {
      setIsOpen(open);
    },
    [setIsOpen]
  );

  const handleTryNowClick = useCallback(() => {
    setIsOpen(false);
    setOpenSourceControlPopover(true);
  }, [setIsOpen, setOpenSourceControlPopover]);

  if (!isFlowEnabled || !shouldShowSpotlight) return <>{children}</>;

  return (
    <Spotlight open={isOpen} onOpenChange={handleOpenChange}>
      <SpotlightAnchor asChild>{children}</SpotlightAnchor>
      <SpotlightContent
        align="start"
        alignOffset={0}
        side="top"
        sideOffset={SPOTLIGHT_SIDE_OFFSET}
        className="w-62 max-w-[calc(100vw-2rem)] overflow-hidden rounded-xl border-0 p-0 shadow-lg"
        data-testid="sources-spotlight"
      >
        <SpotlightArrow width={12} height={8} />
        <SpotlightHeader className="rounded-t-xl px-4 pt-3">
          <SpotlightTitle className="flex-1">
            {t('chat.onboarding.sourcesSpotlight.title')}
          </SpotlightTitle>
          <SpotlightClose />
        </SpotlightHeader>
        <SpotlightImage className="relative h-[144px] w-full overflow-hidden bg-surface">
          <Image
            src={sourceControlSpotlightVisual}
            alt=""
            fill
            className="object-contain"
          />
        </SpotlightImage>
        <SpotlightFooter className="flex flex-col gap-3 rounded-b-xl bg-callout-background px-4 pb-3 text-text-primary-on-dark">
          <SpotlightDescription className="p-0 text-left">
            {t('chat.onboarding.sourcesSpotlight.description')}
          </SpotlightDescription>
          <div className="flex w-full items-center justify-between">
            <SpotlightProgress>3/3</SpotlightProgress>
            <Button
              variant="secondary"
              size="sm"
              onClick={handleTryNowClick}
              className="bg-surface text-text-primary hover:bg-background-secondary"
            >
              {t('chat.onboarding.sourcesSpotlight.tryNow')}
            </Button>
          </div>
        </SpotlightFooter>
      </SpotlightContent>
    </Spotlight>
  );
};
